import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@chakra-ui/react';
import { DollarSign, Loader2, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { getBalanceTransReportingData } from '../actions/transactionReportActions';
import CascadingDropdownFilter from '../components/CascadingDropdownFilter';
import styles from '../styles/BalanceTransactionReport.module.css';
import { useFieldPrivilege } from '../hooks/useFieldPrivilege';

const ROLE_HIERARCHY = [
  { key: 'company_id', label: 'Company', role: 'company' },
  { key: 'admin_id', label: 'Admin', role: 'admin' },
  { key: 'super_stokez_id', label: 'Super Stokez', role: 'super_stokez' },
  { key: 'stokez_id', label: 'Stokez', role: 'stokez' },
  { key: 'agent_id', label: 'Agent', role: 'agent' },
  { key: 'player_id', label: 'Player', role: 'player' },
];

const BalanceTransactionReport = () => {
  const dispatch = useDispatch();
  const { canShowField } = useFieldPrivilege();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [roleFilter, setRoleFilter] = useState({});

  const { balanceLogData, loading, error } = useSelector((state) => {
    return state.balanceLog;
  });

  const showSlno = canShowField('reportTransaction', 'slno', 'list');
  const showTransTime = canShowField('reportTransaction', 'transTime', 'list');
  const showCreditId = canShowField('reportTransaction', 'credit_id', 'list');
  const showDebitId = canShowField('reportTransaction', 'debit_id', 'list');
  const showBalanceTrans = canShowField('reportTransaction', 'balance_trans', 'list');
  const showCurrBalance = canShowField('reportTransaction', 'curr_balance', 'list');
  const showTransferBy = canShowField('reportTransaction', 'transferBy', 'list');
  const visibleColCount = [
    showSlno, showTransTime, showCreditId, showDebitId,
    showBalanceTrans, showCurrBalance, showTransferBy,
  ].filter(Boolean).length;

  const handleSelectionChange = useCallback((selections) => {
    setRoleFilter(selections || {});
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    dispatch(getBalanceTransReportingData(roleFilter));
  }, [dispatch, roleFilter]);

  const data = useMemo(() => {
    return Array.isArray(balanceLogData) ? balanceLogData : [];
  }, [balanceLogData]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize]);

  const totalPages = useMemo(() => {
    return Math.ceil(data.length / pageSize) || 1;
  }, [data.length, pageSize]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (e) => {
    const newPageSize = parseInt(e.target.value, 10);
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageInput = e.target.querySelector('input[type="number"]');
    if (pageInput) {
      const page = parseInt(pageInput.value, 10);
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    }
  };

  return (
    <div className={styles.balanceTransactionReport}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <DollarSign size={24} className={styles.headerIcon} />
          <h1 className={styles.title}>View Balance Transaction Report</h1>
        </div>
      </div>

      <Box mb={4} p={3} bg="white" borderRadius="md" borderWidth="1px" borderColor="gray.200">
        <CascadingDropdownFilter
          onSelectionChange={handleSelectionChange}
          initialValues={{}}
          userHierarchy={ROLE_HIERARCHY}
          roleBeingCreated="player"
        />
      </Box>

      {loading ? (
        <div className={styles.loadingContainer}>
          <Loader2 className={styles.spinner} size={48} />
        </div>
      ) : error ? (
        <div className={styles.errorAlert}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  {showSlno && <th>SL NO</th>}
                  {showTransTime && <th>Trans Time</th>}
                  {showCreditId && <th>Credit ID</th>}
                  {showDebitId && <th>Debit ID</th>}
                  {showBalanceTrans && <th>Points</th>}
                  {showCurrBalance && <th>Curr Balance</th>}
                  {showTransferBy && <th>Transfer By</th>}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColCount || 1} className={styles.emptyState}>
                      No transaction records found.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, index) => {
                    const globalIndex = (currentPage - 1) * pageSize + index;
                    return (
                      <tr key={index}>
                        {showSlno && <td>{row.slno || globalIndex + 1}</td>}
                        {showTransTime && <td>{row.transTime ? new Date(row.transTime).toLocaleString() : '-'}</td>}
                        {showCreditId && <td>{row.credit_id || '-'}</td>}
                        {showDebitId && <td>{row.debit_id || '-'}</td>}
                        {showBalanceTrans && <td>{row.balance_trans || '-'}</td>}
                        {showCurrBalance && <td>{row.curr_balance || '-'}</td>}
                        {showTransferBy && <td>{row.transferBy || '-'}</td>}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {data.length > 0 && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationLeft}>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  title="First Page"
                  aria-label="First Page"
                >
                  <ChevronsLeft size={16} />
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  title="Previous Page"
                  aria-label="Previous Page"
                >
                  <ChevronLeft size={16} />
                </button>
              </div>

              <div className={styles.paginationCenter}>
                <span className={styles.paginationText}>
                  Page{' '}
                  <strong>{currentPage}</strong>
                  {' '}of{' '}
                  <strong>{totalPages}</strong>
                </span>
                <form onSubmit={handleGoToPage} className={styles.goToPageForm}>
                  <span className={styles.paginationText}>Go to page:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    defaultValue={currentPage}
                    className={styles.pageInput}
                    aria-label="Page number"
                  />
                  <button type="submit" className={styles.goToPageButton}>
                    Go
                  </button>
                </form>
                <div className={styles.pageSizeSelector}>
                  <label htmlFor="pageSize" className={styles.paginationText}>Show:</label>
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className={styles.pageSizeSelect}
                  >
                    {[10, 20, 30, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.paginationRight}>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  title="Next Page"
                  aria-label="Next Page"
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  className={styles.paginationButton}
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  title="Last Page"
                  aria-label="Last Page"
                >
                  <ChevronsRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BalanceTransactionReport;
